import { factoredInteger } from './util';

/**
 * 회전 타입. 숫자는 [수평0, 수직(높이축), 수평2] 순서로 매핑되는 치수 배열을 결정한다.
 * getDimension()의 index 1(가운데)이 "수직(높이)" 축이다.
 */
export const RotationType_WHD = 0;
export const RotationType_HWD = 1;
export const RotationType_HDW = 2;
export const RotationType_DHW = 3;
export const RotationType_DWH = 4;
export const RotationType_WDH = 5;

// position / dimension 배열의 축 인덱스
export const WidthAxis = 0;
export const HeightAxis = 1;
export const DepthAxis = 2;

export const StartPosition = [0, 0, 0];

export const RotationTypeStrings = {
  [RotationType_WHD]: 'RotationType_WHD (w,h,d)',
  [RotationType_HWD]: 'RotationType_HWD (h,w,d)',
  [RotationType_HDW]: 'RotationType_HDW (h,d,w)',
  [RotationType_DHW]: 'RotationType_DHW (d,h,w)',
  [RotationType_DWH]: 'RotationType_DWH (d,w,h)',
  [RotationType_WDH]: 'RotationType_WDH (w,d,h)',
};

export default class Item {

  name = '';
  width = 0;
  height = 0;
  depth = 0;
  weight = 0;
  allowedRotation = [0, 1, 2, 3, 4, 5];

  rotationType = RotationType_WHD;
  position = []; // x, y, z (min-corner)

  constructor(name, w, h, d, wg, allowedRotation, mustBeFlat = false) {
    this.name = name;
    this.width = factoredInteger(w);
    this.height = factoredInteger(h);
    this.depth = factoredInteger(d);
    this.weight = factoredInteger(wg);

    this.mustBeFlat = mustBeFlat;

    this.allowedRotation = allowedRotation ? allowedRotation : this.allowedRotation;

    // 회전 별 치수 캐싱 (getDimension()과 동일한 순서)
    this.rotationDimensions = {
      [RotationType_WHD]: [this.width, this.height, this.depth],
      [RotationType_HWD]: [this.height, this.width, this.depth],
      [RotationType_HDW]: [this.height, this.depth, this.width],
      [RotationType_DHW]: [this.depth, this.height, this.width],
      [RotationType_DWH]: [this.depth, this.width, this.height],
      [RotationType_WDH]: [this.width, this.depth, this.height],
    };

    // mustBeFlat: 가장 짧은 변을 수직(높이축)에 두는 회전만 허용해 눕혀 놓는다.
    // 이 프로젝트는 상자 생성 시 w=최장 / h=최단 / d=중간 으로 정렬해 넣으므로,
    // 수직 축(index 1)이 h(최단)가 되는 회전은 WHD(0)=[w,h,d] 와 DHW(3)=[d,h,w] 뿐이다.
    if (this.mustBeFlat) {
      this.allowedRotation = this.allowedRotation.filter(r => r === RotationType_WHD || r === RotationType_DHW);
    }
  }

  getWidth() { return this.width; }
  getHeight() { return this.height; }
  getDepth() { return this.depth; }
  getWeight() { return this.weight; }

  getRotationType() { return this.rotationType; }
  getAllowedRotation() { return this.allowedRotation; }
  getRotationTypeString() { return RotationTypeStrings[this.getRotationType()]; }

  /**
   * 현재 회전 상태의 [수평(폭), 수직(높이), 수평(깊이)] 치수.
   * 캐시가 있으면 캐시를, 없으면 즉석 계산한다.
   */
  getDimension() {
    const cached = this.rotationDimensions?.[this.rotationType];
    if (cached) {
      return cached;
    }
    switch (this.rotationType) {
      case RotationType_WHD: return [this.width, this.height, this.depth];
      case RotationType_HWD: return [this.height, this.width, this.depth];
      case RotationType_HDW: return [this.height, this.depth, this.width];
      case RotationType_DHW: return [this.depth, this.height, this.width];
      case RotationType_DWH: return [this.depth, this.width, this.height];
      case RotationType_WDH: return [this.width, this.depth, this.height];
      default: return [this.width, this.height, this.depth];
    }
  }

  /** 바닥 면적에 사용하는 [폭, 깊이] (수직축 제외) */
  getFootprint() {
    const d = this.getDimension();
    return [d[WidthAxis], d[DepthAxis]];
  }

  intersect(i2) {
    return rectIntersect(this, i2, WidthAxis, HeightAxis) &&
      rectIntersect(this, i2, HeightAxis, DepthAxis) &&
      rectIntersect(this, i2, WidthAxis, DepthAxis);
  }

  getVolume() {
    return this.getWidth() * this.getDepth() * this.getHeight();
  }

  getWidthDepthVolume() {
    return this.getWidth() * this.getDepth();
  }

  toString() {
    return `Item:${this.name} (${this.getRotationTypeString()} = ${this.getDimension().join('x')}, Wg. = ${this.weight})`;
  }
}

export const rectIntersect = (i1, i2, x, y) => {
  let d1, d2, cx1, cy1, cx2, cy2, ix, iy;

  d1 = i1.getDimension();
  d2 = i2.getDimension();

  cx1 = i1.position[x] + d1[x] / 2;
  cy1 = i1.position[y] + d1[y] / 2;
  cx2 = i2.position[x] + d2[x] / 2;
  cy2 = i2.position[y] + d2[y] / 2;

  ix = Math.max(cx1, cx2) - Math.min(cx1, cx2);
  iy = Math.max(cy1, cy2) - Math.min(cy1, cy2);

  return ix < (d1[x] + d2[x]) / 2 && iy < (d1[y] + d2[y]) / 2;
};
